import {
  Button,
  FormRow,
  IconButton,
  Input,
  Paragraph,
  Spacer,
  Stack,
  Wizard,
  useWizardPage,
  Link,
  Title
} from '@efast_public/fjd-component-library';
import React, { useCallback, useEffect } from 'react';
import { ReactSortable } from 'react-sortablejs';
import { v4 as uuidv4 } from 'uuid';
import './FooterLinks.css';
import FooterDto from '../../../../api/models/Footer/FooterDto';
import { Trans, useTranslation } from 'react-i18next';
import { useFieldArray, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi/dist/joi';
import Joi from 'joi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BehApiRoutes,
  behBaseFooterUrl,
  behClient
} from '../../../../api/beh-api';
import { useNotification } from '../../../../shared/notification/useNotification';
import FooterListDto from '../../../../api/models/Footer/FooterListDto';
import {
  FJD_DOCS_SETTINGS_FOOTERLINKS,
  FJD_DOCS_EINRICHTUNGSASSISTANT
} from '../../../../shared/utils/Links';
import './FooterLinks.css';

type FooterDtoDefault = Pick<FooterDto, 'title' | 'url'>;

export interface CustomizableFooter extends Omit<FooterDto, 'id'> {
  id: string;
  chosen?: boolean;
}

interface FooterForm {
  footer: Array<CustomizableFooter>;
}

const footerLinkDefault: FooterDtoDefault = {
  title: '',
  url: ''
};

const prohbitedNames = [
  'Impressum',
  'Datenschutzerklärung',
  'Erklärung zur Barrierefreiheit',
  'Barrierefreiheit',
  'Barrierefreiheitserklärung'
];

export default function FooterLinks({
  isSettings = false
}: {
  readonly isSettings?: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyUserFromRequest } = useNotification();
  const { data: footers } = useQuery<Array<FooterDto>, Error>({
    queryKey: [BehApiRoutes.footer()],
    queryFn: () =>
      notifyUserFromRequest(() =>
        behClient.get(behBaseFooterUrl + BehApiRoutes.footer())
      )
  });
  const { mutateAsync: postFooterLinksAndUpdateCache } = useMutation({
    mutationFn: (payload: FooterListDto) =>
      notifyUserFromRequest(
        () => behClient.post(behBaseFooterUrl + BehApiRoutes.footer(), payload),
        {
          successMessage: t('api.success.updateFooter'),
          errorMessage: t('api.error.updateFooter')
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BehApiRoutes.footer()] });
    }
  });

  const {
    formState: { errors, isDirty, isValid },
    control,
    register,
    getValues,
    reset,
    trigger,
    clearErrors
  } = useForm<FooterForm>({
    mode: 'onBlur',
    resolver: joiResolver(
      Joi.object({
        footer: Joi.array<CustomizableFooter>()
          .items(
            Joi.object<CustomizableFooter>({
              title: Joi.string()
                .invalid(...prohbitedNames)
                .insensitive()
                .max(30)
                .required()
                .messages({
                  'string.max': t('footerLinks.string.max'),
                  'string.empty': t('footerLinks.string.empty'),
                  'any.invalid': t('footerLinks.any.invalid')
                }),
              url: Joi.string()
                .uri()
                .message(t('uri.required'))
                .regex(new RegExp('^https?://'))
                .message(t('uri.prefix.required'))
                .required()
                .messages({
                  'string.empty': t('string.empty')
                }),
              id: Joi.optional(),
              order: Joi.optional(),
              footerType: Joi.optional(),
              version: Joi.optional(),
              chosen: Joi.boolean().optional()
            })
          )
          .unique((a, b) => a.title === b.title)
          .messages({
            'array.unique': t('footerLinks.array.unique')
          })
      })
    )
  });

  const { fields, append, replace } = useFieldArray({
    control,
    name: 'footer',
    keyName: 'uuid'
  });

  useEffect(() => {
    if (footers) {
      const filtered: Array<CustomizableFooter> = footers.filter(
        (link: FooterDto): link is CustomizableFooter =>
          link.footerType === 'CUSTOMIZABLE' && link.id !== null
      );
      reset({ footer: filtered });
    }
  }, [footers, reset]);

  const addBlankFooterLink = () => {
    if (fields.length === 7) {
      return;
    }
    append({
      order: fields.length + 1,
      id: uuidv4(),
      ...footerLinkDefault
    });
  };

  const deleteFooterLink = (index: number) => {
    const updatedFooter = getValues()
      .footer.filter((_, footerIndex) => footerIndex !== index)
      .map((f, footerIndex) => ({ ...f, order: footerIndex + 1 }));
    replace(updatedFooter);
    clearErrors(`footer.${index}`);
  };

  const postFooters = useCallback(() => {
    if (isDirty) {
      return postFooterLinksAndUpdateCache({
        footerDtos: getValues().footer.map((field) => field)
      });
    }
    return Promise.resolve();
  }, [getValues, postFooterLinksAndUpdateCache, isDirty]);

  const { wizardState } = useWizardPage({
    isFormValid: isValid,
    headline: (state) =>
      `Schritt ${state.currentPageIndex + 1} von ${state.pagesLength}`,
    onSubmit: postFooters,
    skipButtonLabel: 'Später einrichten',
    isStepOptional: true
  });

  const content = (
    <Stack spacing="m">
      <Title level={2} visualLevel={4}>
        {t('configAssistent.footerLinks.headline')}
      </Title>
      <Paragraph>
        <Trans
          i18nKey="footerLinks.settings.descriptionText"
          components={{
            strong: <strong />
          }}
        />
        <Trans
          i18nKey="configAssistent.configAssistentDocumentationNotice"
          components={{
            Link: (
              <Link
                href={
                  isSettings
                    ? FJD_DOCS_SETTINGS_FOOTERLINKS
                    : FJD_DOCS_EINRICHTUNGSASSISTANT
                }
                target="_blank"
              >
                hier
              </Link>
            )
          }}
        />
      </Paragraph>
      <Spacer size="s" />
      <ReactSortable
        animation={200}
        handle="[data-handle]"
        list={(getValues()?.footer ?? []).map(
          (footerDto: CustomizableFooter) => ({
            ...footerDto,
            chosen: true
          })
        )}
        setList={(newState) => {
          replace(
            newState.map((entry, index) => ({ ...entry, order: index + 1 }))
          );
        }}
      >
        {fields.map((footerListItem, index) => (
          <Stack key={footerListItem.id} spacing="s">
            <Stack
              className="footerInputRow"
              direction={'row'}
              spacing="s"
              key={footerListItem.id}
            >
              <IconButton
                className="footerlink-icon-button"
                id={`drag_${footerListItem.id}`}
                icon="draggable"
                label="Tertiary"
                variant="primary-text"
                data-handle
                aria-label={`Footer Link verschieben ${footerListItem.id}`}
              />
              <FormRow
                label="Bezeichnung Footer-Link"
                className="footerLinkRowInput"
                validationMessage={
                  errors.footer?.[index]?.message ||
                  errors.footer?.[index]?.title?.message
                }
                variant={
                  ((errors.footer?.[index]?.message ||
                    errors.footer?.[index]?.title?.message) &&
                    'error') ||
                  undefined
                }
              >
                <Input
                  {...register(`footer.${index}.title`)}
                  id={`title_order_${footerListItem.title}`}
                  placeholder="z.B. Hilfe, Kontakt"
                  type="text"
                  onBlur={() => trigger().catch(console.error)}
                  className="bezInputRow"
                  aria-valuetext={footerListItem.title}
                />
              </FormRow>
              <FormRow
                label="Ziel-URL"
                className="footerLinkRowInput"
                validationMessage={errors.footer?.[index]?.url?.message}
                variant={
                  errors.footer?.[index]?.url?.message ? 'error' : undefined
                }
              >
                <Input
                  {...register(`footer.${index}.url`)}
                  id={`url_order_${footerListItem.order}`}
                  placeholder="z.B. https://efast.digital"
                  type="url"
                  aria-valuetext={footerListItem.url}
                />
              </FormRow>
              <IconButton
                className="footerlink-icon-button"
                id={`delete_${footerListItem.id}`}
                icon="trashcan"
                label="Tertiary"
                variant="primary-text"
                aria-label={`Footer Link löschen ${footerListItem.order}`}
                onClick={() => deleteFooterLink(index)}
              />
            </Stack>
            <Spacer />
          </Stack>
        ))}
      </ReactSortable>
      <Spacer />
      <span className="footerlinksButtonContainer">
        <Button
          variant="primary-text"
          disabled={fields.length >= 7}
          beforeIcon="add-outline"
          onClick={() => addBlankFooterLink()}
        >
          Neuen Link hinzufügen
        </Button>
        {isSettings && (
          <Button
            variant="primary"
            data-testid="saveFooterLinks"
            onClick={() => {
              trigger()
                .then((valid) => {
                  if (valid) {
                    postFooters().catch(() => {});
                  }
                })
                .catch(console.error);
            }}
          >
            Footerlinks speichern
          </Button>
        )}
      </span>
    </Stack>
  );

  if (isSettings) {
    return content;
  }

  return <Wizard.Page>{content}</Wizard.Page>;
}
